import React from "react";
import { MoodType } from "../types/types.ts";

interface ChatbotAvatarProps {
  mood: MoodType;
  size?: number | string;
  className?: string;
}

type DialoguinMood =
  | "happy"
  | "searching"
  | "thinking"
  | "angry"
  | "laughing"
  | "surprised";

const MOODS: DialoguinMood[] = [
  "happy",
  "searching",
  "thinking",
  "angry",
  "laughing",
  "surprised",
];

const isDialoguinMood = (mood: MoodType): mood is DialoguinMood => {
  return MOODS.includes(mood as DialoguinMood);
};

const ChatbotAvatar: React.FC<ChatbotAvatarProps> = ({
  mood,
  size = "100%",
  className,
}) => {
  const validMood: DialoguinMood = isDialoguinMood(mood) ? mood : "happy";

  const show = (targetMood: DialoguinMood): React.CSSProperties => ({
    display: validMood === targetMood ? "block" : "none",
  });

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 240 240"
      role="img"
      aria-label={`Dialoguin avatar ${validMood}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="faceSoftLight" x1="64" y1="54" x2="178" y2="178" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFD9B7" />
          <stop offset="1" stopColor="#FFC39F" />
        </linearGradient>

        <linearGradient id="headsetPad" x1="25" y1="78" x2="54" y2="142" gradientUnits="userSpaceOnUse">
          <stop stopColor="#3D4652" />
          <stop offset="1" stopColor="#202833" />
        </linearGradient>

        <linearGradient id="lensGradient" x1="55" y1="75" x2="100" y2="122" gradientUnits="userSpaceOnUse">
          <stop stopColor="#EFFAFF" />
          <stop offset="1" stopColor="#C7E9F8" />
        </linearGradient>

        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="6" stdDeviation="5" floodColor="#0F172A" floodOpacity="0.18" />
        </filter>
      </defs>

      <style>
        {`
          .dialoguin-shell {
            transform-box: fill-box;
            transform-origin: center;
          }

          .dialoguin-happy {
            animation: dialoguin-bounce 2.4s ease-in-out infinite;
          }

          .dialoguin-searching {
            animation: dialoguin-search 3.2s ease-in-out infinite;
          }

          .dialoguin-thinking {
            animation: dialoguin-think 3s ease-in-out infinite;
          }

          .dialoguin-angry {
            animation: dialoguin-shake 0.55s ease-in-out infinite;
          }

          .dialoguin-laughing {
            animation: dialoguin-laugh 1.1s ease-in-out infinite;
          }

          .dialoguin-surprised {
            animation: dialoguin-pop 1.8s ease-in-out infinite;
          }

          .dialoguin-eye-looking {
            animation: dialoguin-look 2.8s ease-in-out infinite;
          }

          .dialoguin-magnifier {
            transform-box: fill-box;
            transform-origin: 50% 50%;
            animation: dialoguin-magnify 2.4s ease-in-out infinite;
          }

          .dialoguin-thought-small {
            animation: dialoguin-bubble 1.9s ease-in-out infinite;
          }

          .dialoguin-thought-medium {
            animation: dialoguin-bubble 1.9s ease-in-out infinite 0.22s;
          }

          .dialoguin-thought-main {
            animation: dialoguin-bubble 1.9s ease-in-out infinite 0.44s;
          }

          .dialoguin-alert-mark {
            transform-box: fill-box;
            transform-origin: center;
            animation: dialoguin-alert 1.2s ease-in-out infinite;
          }

          @keyframes dialoguin-bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }

          @keyframes dialoguin-search {
            0%, 100% { transform: rotate(0deg) translate(0, 0); }
            35% { transform: rotate(-2deg) translate(-2px, -2px); }
            70% { transform: rotate(2deg) translate(2px, -1px); }
          }

          @keyframes dialoguin-think {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            50% { transform: translate(2px, 2px) rotate(1.5deg); }
          }

          @keyframes dialoguin-shake {
            0%, 100% { transform: translateX(0) rotate(0deg); }
            25% { transform: translateX(-2px) rotate(-1deg); }
            75% { transform: translateX(2px) rotate(1deg); }
          }

          @keyframes dialoguin-laugh {
            0%, 100% { transform: scale(1) rotate(0deg); }
            50% { transform: scale(1.035) rotate(1.5deg); }
          }

          @keyframes dialoguin-pop {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.03); }
          }

          @keyframes dialoguin-look {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(4px, -3px); }
          }

          @keyframes dialoguin-magnify {
            0%, 100% { transform: translate(0, 0) rotate(-5deg) scale(1); }
            50% { transform: translate(-3px, 4px) rotate(4deg) scale(1.07); }
          }

          @keyframes dialoguin-bubble {
            0%, 100% { opacity: 0.35; transform: translateY(2px) scale(0.94); }
            45%, 70% { opacity: 1; transform: translateY(-1px) scale(1); }
          }

          @keyframes dialoguin-alert {
            0%, 100% { opacity: 0.7; transform: scale(1) rotate(0deg); }
            50% { opacity: 1; transform: scale(1.08) rotate(-3deg); }
          }
        `}
      </style>

      <g style={show("happy")} className="dialoguin-shell dialoguin-happy">
        <AvatarBase />
        <HappyFace />
      </g>

      <g style={show("searching")} className="dialoguin-shell dialoguin-searching">
        <AvatarBase />
        <SearchingFace />
        <Magnifier />
      </g>

      <g style={show("thinking")} className="dialoguin-shell dialoguin-thinking">
        <AvatarBase />
        <ThinkingFace />
        <ThinkingHand />
        <ThoughtBubbles />
      </g>

      <g style={show("angry")} className="dialoguin-shell dialoguin-angry">
        <AvatarBase />
        <AngryFace />
        <AngryMarks />
      </g>

      <g style={show("laughing")} className="dialoguin-shell dialoguin-laughing">
        <AvatarBase />
        <LaughingFace />
        <LaughMarks />
      </g>

      <g style={show("surprised")} className="dialoguin-shell dialoguin-surprised">
        <AvatarBase />
        <SurprisedFace />
        <SurpriseMark />
      </g>
    </svg>
  );
};

const AvatarBase: React.FC = () => (
  <g filter="url(#softShadow)">
    <ellipse cx="120" cy="199" rx="67" ry="12" fill="#0F172A" opacity="0.12" />

    <path
      d="M48 118C48 75.5 80.5 48 121 48C161.5 48 193 75.5 193 118C193 158.5 162.5 178 121 178H96.5L69.5 198C65.7 200.8 60.5 198.1 60.5 193.4V172.5C52.4 162.1 48 144.2 48 118Z"
      fill="url(#faceSoftLight)"
      stroke="#101820"
      strokeWidth="5"
      strokeLinejoin="round"
    />

    <path
      d="M38 104C38 62 72 31 120 31C168 31 202 62 202 104"
      fill="none"
      stroke="#101820"
      strokeWidth="14"
      strokeLinecap="round"
    />
    <path
      d="M43 104C43 66 74 38 120 38C166 38 197 66 197 104"
      fill="none"
      stroke="#394150"
      strokeWidth="8"
      strokeLinecap="round"
    />

    <rect x="27" y="88" width="28" height="58" rx="13" fill="url(#headsetPad)" stroke="#101820" strokeWidth="4" />
    <rect x="185" y="88" width="28" height="58" rx="13" fill="url(#headsetPad)" stroke="#101820" strokeWidth="4" />

    <path
      d="M200 139C200 161 188 176 168 179"
      fill="none"
      stroke="#101820"
      strokeWidth="6"
      strokeLinecap="round"
    />
    <path
      d="M191 143C190 156 183 166 170 170"
      fill="none"
      stroke="#F8FAFC"
      strokeWidth="3"
      strokeLinecap="round"
      opacity="0.85"
    />
    <rect x="153" y="160" width="38" height="20" rx="9" fill="#252D38" stroke="#101820" strokeWidth="4" />

    <circle cx="84" cy="134" r="10" fill="#F47F70" opacity="0.62" />
    <circle cx="156" cy="134" r="10" fill="#F47F70" opacity="0.62" />
  </g>
);

const Eye: React.FC<{ cx: number; cy?: number; rx?: number; ry?: number }> = ({
  cx,
  cy = 105,
  rx = 8.5,
  ry = 13,
}) => (
  <g>
    <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#05080D" />
    <ellipse cx={cx - 3} cy={cy - 6} rx="2.5" ry="4" fill="#FFFFFF" opacity="0.88" />
  </g>
);

const HappyFace: React.FC = () => (
  <g>
    <path d="M78 84Q86 76 96 84" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <path d="M144 84Q152 76 162 84" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <Eye cx={93} />
    <Eye cx={147} />
    <path d="M98 132Q120 151 142 132" fill="none" stroke="#101820" strokeWidth="6" strokeLinecap="round" />
  </g>
);

const SearchingFace: React.FC = () => (
  <g>
    <path d="M78 84Q86 77 96 84" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <path d="M143 80Q154 78 162 87" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <g className="dialoguin-eye-looking">
      <Eye cx={96} cy={105} />
      <Eye cx={151} cy={105} />
    </g>
    <ellipse cx="124" cy="137" rx="5" ry="8" fill="#101820" />
    <ellipse cx="124" cy="140" rx="2.5" ry="3.2" fill="#FF8173" />
  </g>
);

const ThinkingFace: React.FC = () => (
  <g>
    <path d="M78 86Q86 78 96 86" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <path d="M143 84Q152 78 162 86" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <g className="dialoguin-eye-looking">
      <Eye cx={94} cy={106} />
      <Eye cx={148} cy={106} />
    </g>
    <path d="M109 139Q122 131 134 140" fill="none" stroke="#101820" strokeWidth="6" strokeLinecap="round" />
  </g>
);

const AngryFace: React.FC = () => (
  <g>
    <path d="M76 86L101 98" fill="none" stroke="#101820" strokeWidth="7" strokeLinecap="round" />
    <path d="M164 86L139 98" fill="none" stroke="#101820" strokeWidth="7" strokeLinecap="round" />
    <Eye cx={92} cy={110} rx={8} ry={12} />
    <Eye cx={148} cy={110} rx={8} ry={12} />
    <path d="M99 148Q120 132 141 148" fill="none" stroke="#101820" strokeWidth="7" strokeLinecap="round" />
  </g>
);

const LaughingFace: React.FC = () => (
  <g>
    <path d="M79 100Q91 87 104 100" fill="none" stroke="#101820" strokeWidth="6" strokeLinecap="round" />
    <path d="M136 100Q149 87 161 100" fill="none" stroke="#101820" strokeWidth="6" strokeLinecap="round" />

    <path
      d="M91 128Q120 166 149 128Q139 153 120 153Q101 153 91 128Z"
      fill="#101820"
      stroke="#101820"
      strokeWidth="5"
      strokeLinejoin="round"
    />
    <path
      d="M104 149Q120 139 136 149Q128 157 120 157Q112 157 104 149Z"
      fill="#FF786D"
    />
  </g>
);

const SurprisedFace: React.FC = () => (
  <g>
    <path d="M79 85Q88 76 98 84" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <path d="M142 84Q152 76 162 85" fill="none" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
    <Eye cx={94} cy={107} rx={9} ry={14} />
    <Eye cx={148} cy={107} rx={9} ry={14} />
    <ellipse cx="121" cy="143" rx="11" ry="16" fill="#101820" />
    <ellipse cx="121" cy="150" rx="6.5" ry="6" fill="#FF786D" />
  </g>
);

const Magnifier: React.FC = () => (
  <g className="dialoguin-magnifier">
    <circle cx="72" cy="116" r="27" fill="url(#lensGradient)" stroke="#101820" strokeWidth="6" />
    <path d="M91 135L109 154" stroke="#101820" strokeWidth="8" strokeLinecap="round" />
    <path d="M61 102Q68 93 80 93" fill="none" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" opacity="0.9" />
    <path
      d="M55 154C45 154 39 164 42 174C44 183 51 189 60 188C70 187 75 180 75 171V161C75 157 72 154 68 154H55Z"
      fill="url(#faceSoftLight)"
      stroke="#101820"
      strokeWidth="4"
      strokeLinejoin="round"
    />
  </g>
);

const ThinkingHand: React.FC = () => (
  <g>
    <path
      d="M68 149C59 153 56 166 62 175C68 184 81 184 87 176L94 166C98 160 95 152 88 151L81 150L85 140C87 135 82 131 78 135L68 149Z"
      fill="url(#faceSoftLight)"
      stroke="#101820"
      strokeWidth="4"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    <path d="M78 151L92 137" stroke="#101820" strokeWidth="5" strokeLinecap="round" />
  </g>
);

const ThoughtBubbles: React.FC = () => (
  <g>
    <circle className="dialoguin-thought-small" cx="180" cy="79" r="7" fill="#FFFFFF" stroke="#101820" strokeWidth="4" />
    <circle className="dialoguin-thought-medium" cx="197" cy="58" r="10" fill="#FFFFFF" stroke="#101820" strokeWidth="4" />
    <path
      className="dialoguin-thought-main"
      d="M180 25C184 13 197 8 208 13C218 5 234 10 239 22C250 25 256 36 252 47C256 58 248 69 236 70C231 82 215 86 205 78C194 82 181 75 179 64C166 60 161 45 169 35C171 31 175 28 180 25Z"
      transform="translate(-32 3) scale(0.82)"
      fill="#FFFFFF"
      stroke="#101820"
      strokeWidth="5"
      strokeLinejoin="round"
    />
    <text
      x="142"
      y="56"
      fill="#202833"
      fontSize="36"
      fontWeight="900"
      textAnchor="middle"
      dominantBaseline="middle"
      fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    >
      ?
    </text>
  </g>
);

const AngryMarks: React.FC = () => (
  <g className="dialoguin-alert-mark" fill="none" stroke="#F36F61" strokeWidth="7" strokeLinecap="round">
    <path d="M42 54L29 39" />
    <path d="M55 49L49 30" />
    <path d="M31 70L14 61" />
  </g>
);

const LaughMarks: React.FC = () => (
  <g className="dialoguin-alert-mark" fill="none" stroke="#F36F61" strokeWidth="7" strokeLinecap="round">
    <path d="M174 46Q187 47 195 58" />
    <path d="M188 33L184 65" />
    <path d="M166 60L200 43" />
  </g>
);

const SurpriseMark: React.FC = () => (
  <g className="dialoguin-alert-mark" fill="#F36F61">
    <rect x="197" y="39" width="13" height="42" rx="6.5" transform="rotate(9 203.5 60)" />
    <circle cx="198" cy="95" r="7" />
  </g>
);

export default ChatbotAvatar;
